/**
 * RoleMatrixBuilder.jsx — Level-based role × feature permission matrix
 *
 * Each feature has ordered access levels (e.g. hidden → view_assigned → full).
 * Each role picks ONE level per feature — no contradictions possible.
 * Super Admin is always locked to the highest level.
 */

import { useState, useEffect, useRef } from 'react';
import { useSettings, useUpdateSettings } from '../../hooks/useSettings';
import { useToast } from '../layout/Toast';

// ── Role Tiers ─────────────────────────────────────────────────────────────
const ROLES = [
  { id: 'super_admin', label: 'Super Admin', color: '#DC2626', short: 'S.Admin', desc: 'Owner (Cyprian)' },
  { id: 'admin',       label: 'Admin',       color: '#F59E0B', short: 'Admin',   desc: 'Ops manager (Jonny)' },
  { id: 'core_crew',   label: 'Core Crew',   color: '#3B82F6', short: 'Core',    desc: 'Regular team (Shay, Sierra)' },
  { id: 'crew',        label: 'Crew',        color: '#6B7280', short: 'Crew',    desc: 'Freelancers, contractors' },
];

// ── Feature Definitions ────────────────────────────────────────────────────
// levels: ordered low→high. Each level includes capabilities of levels below it.
// defaults: what each role starts at.
const FEATURES = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: '🏠',
    levels: [
      { id: 'own_stats',  label: 'Own Stats',  desc: 'Own upcoming shoots & earnings' },
      { id: 'full_stats', label: 'Full Stats',  desc: 'Company revenue, pipeline, P&L overview' },
    ],
    defaults: { super_admin: 'full_stats', admin: 'full_stats', core_crew: 'own_stats', crew: 'own_stats' },
  },
  {
    key: 'jobs',
    label: 'Jobs',
    icon: '📋',
    levels: [
      { id: 'hidden',          label: 'Hidden',           desc: 'No access to jobs' },
      { id: 'view_assigned',   label: 'View Assigned',    desc: 'See assigned jobs (read-only, no revenue)' },
      { id: 'assigned_basic',  label: 'Assigned + Revenue', desc: 'View assigned jobs with revenue, mark shooting' },
      { id: 'assigned_full',   label: 'Full on Assigned', desc: 'Edit, status change, deliver on assigned jobs. Sticky — calc removal doesn\'t revoke.' },
      { id: 'all_edit',        label: 'All + Create/Edit', desc: 'View all jobs, create new, edit any, change status, deliver; delete intake/quoted/confirmed jobs they are or were in the pay split for' },
      { id: 'full',            label: 'Full + Void',       desc: 'Everything above + void/cancel jobs, delete at any status, restore from Trash' },
    ],
    defaults: { super_admin: 'full', admin: 'full', core_crew: 'all_edit', crew: 'assigned_basic' },
  },
  {
    key: 'quotes',
    label: 'Quotes',
    icon: '📝',
    levels: [
      { id: 'hidden',          label: 'Hidden',           desc: 'No access to quotes' },
      { id: 'view_assigned',   label: 'View Assigned',    desc: 'See quotes for jobs they are assigned to' },
      { id: 'create_edit',     label: 'Create & Edit',    desc: 'Create drafts, edit quotes' },
      { id: 'full',            label: 'Full + Send',      desc: 'Create, edit, and send quotes to clients' },
    ],
    defaults: { super_admin: 'full', admin: 'full', core_crew: 'full', crew: 'view_assigned' },
  },
  {
    key: 'calculator',
    label: 'Calculator',
    icon: '🧮',
    levels: [
      { id: 'hidden',       label: 'Hidden',          desc: 'No access to calculator' },
      { id: 'own_earnings', label: 'Own Earnings',     desc: 'See only their own pay in snapshots' },
      { id: 'view_snap',    label: 'View Snapshots',   desc: 'See full pay breakdowns (read-only)' },
      { id: 'full',         label: 'Full Access',      desc: 'Edit splits, assign crew, save' },
    ],
    defaults: { super_admin: 'full', admin: 'full', core_crew: 'full', crew: 'own_earnings' },
  },
  {
    key: 'calendar',
    label: 'Calendar',
    icon: '📅',
    levels: [
      { id: 'own_schedule', label: 'Own Schedule',    desc: 'See only events they are assigned to' },
      { id: 'view_all',     label: 'View All',        desc: 'See all events (read-only)' },
      { id: 'full',         label: 'Full Access',     desc: 'View all + create events and jobs' },
    ],
    defaults: { super_admin: 'full', admin: 'full', core_crew: 'full', crew: 'own_schedule' },
  },
  {
    key: 'crm',
    label: 'People (CRM)',
    icon: '👥',
    levels: [
      { id: 'hidden',           label: 'Hidden',          desc: 'No access to People page' },
      { id: 'view_no_financials', label: 'View (No $)',   desc: 'See names, emails, phones — no revenue or deal history' },
      { id: 'edit_no_financials', label: 'Edit (No $)',   desc: 'Add/edit contacts — no revenue data' },
      { id: 'full',              label: 'Full + Financials', desc: 'All contacts with revenue data & deal history' },
    ],
    defaults: { super_admin: 'full', admin: 'full', core_crew: 'edit_no_financials', crew: 'hidden' },
  },
  {
    key: 'invoices',
    label: 'Invoices',
    icon: '💰',
    levels: [
      { id: 'hidden',          label: 'Hidden',           desc: 'No access to invoices' },
      { id: 'view_assigned',   label: 'View Assigned',    desc: 'Read-only invoices for assigned jobs' },
      { id: 'create',          label: 'Create & Edit',    desc: 'Create, edit, send invoices, record payments (no delete / no view-all)' },
      { id: 'manage_own',      label: '+ Delete Own',     desc: 'Create & Edit, plus delete invoices on jobs they are or were in the pay split for (never Xero-synced ones)' },
      { id: 'full',            label: 'Full Access',      desc: 'Everything, plus delete any invoice (incl. unlinked), view all invoices, restore from Trash' },
    ],
    defaults: { super_admin: 'full', admin: 'full', core_crew: 'manage_own', crew: 'hidden' },
  },
  {
    key: 'payroll',
    label: 'Payroll & Financials',
    icon: '📊',
    levels: [
      { id: 'own_total',      label: 'Own Total',        desc: 'See own pay totals only' },
      { id: 'own_breakdown',  label: 'Own Breakdown',    desc: 'Per-job detail: hours × rate, budget, lead share (own jobs)' },
      { id: 'full',           label: 'Full P&L',         desc: 'All crew pay, financial reports, company P&L' },
    ],
    defaults: { super_admin: 'full', admin: 'full', core_crew: 'own_breakdown', crew: 'own_total' },
  },
  {
    key: 'expenses',
    label: 'Expenses',
    icon: '🧾',
    levels: [
      { id: 'hidden',       label: 'Hidden',          desc: 'No access to expenses' },
      { id: 'submit_own',   label: 'Submit Own',      desc: 'Add & manage own expenses. No static expenses.' },
      { id: 'view_static',  label: '+ View Static',   desc: 'Submit own + see company fixed costs (rent, insurance)' },
      { id: 'full',         label: 'Full Access',     desc: 'Manage all expenses including static/recurring' },
    ],
    defaults: { super_admin: 'full', admin: 'full', core_crew: 'submit_own', crew: 'submit_own' },
  },
  {
    key: 'time',
    label: 'Time Entries',
    icon: '⏱️',
    levels: [
      { id: 'own_approved',  label: 'Own (Approved)',   desc: 'Log & view own time. Entries need admin approval.' },
      { id: 'own_trusted',   label: 'Own (Trusted)',    desc: 'Log & view own time. Auto-approved.' },
      { id: 'full',          label: 'Full Access',      desc: 'View all crew time entries, approve entries' },
    ],
    defaults: { super_admin: 'full', admin: 'full', core_crew: 'own_trusted', crew: 'own_approved' },
  },
  {
    key: 'outreach',
    label: 'Outreach',
    icon: '📣',
    levels: [
      { id: 'hidden', label: 'Hidden',     desc: 'No access' },
      { id: 'full',   label: 'Full Access', desc: 'Dashboard, prospects, templates' },
    ],
    defaults: { super_admin: 'full', admin: 'full', core_crew: 'full', crew: 'hidden' },
  },
  {
    key: 'legal',
    label: 'Legal',
    icon: '⚖️',
    levels: [
      { id: 'own_contracts', label: 'Own Contracts', desc: 'View contracts you created or are party to' },
      { id: 'full',          label: 'Full + ICA Gen', desc: 'View all contracts, generate ICAs for any crew' },
    ],
    defaults: { super_admin: 'full', admin: 'full', core_crew: 'own_contracts', crew: 'own_contracts' },
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: '⚙️',
    levels: [
      { id: 'profile_only',  label: 'Profile Only',    desc: 'Edit own profile, password, preferences' },
      { id: 'company',       label: '+ Company/Team',  desc: 'Company info, team roster, crew rates, email templates' },
      { id: 'full',          label: 'Full + Perms',    desc: 'Everything + permissions management + data export' },
    ],
    defaults: { super_admin: 'full', admin: 'profile_only', core_crew: 'profile_only', crew: 'profile_only' },
  },
  {
    key: 'wiki',
    label: 'How To',
    icon: '🎓',
    levels: [
      { id: 'hidden', label: 'Hidden',     desc: 'No access to the How To page' },
      { id: 'view',   label: 'View',       desc: 'Browse how-to videos, inspiration, and notes' },
      { id: 'edit',   label: 'Edit',       desc: 'Create and edit entries (no delete)' },
      { id: 'full',   label: 'Full + Delete', desc: 'Create, edit, and delete any entry' },
    ],
    defaults: { super_admin: 'full', admin: 'full', core_crew: 'edit', crew: 'view' },
  },
  {
    key: 'tools',
    label: 'Admin-Only Tools',
    icon: '🔒',
    desc: 'Each tool is independent (not hierarchical)',
    independent: true,
    items: [
      { id: 'voicenotes', label: 'Voice Notes',    desc: 'Voicenote triaging & implementation queue' },
      { id: 'vehicle',    label: 'Vehicle',         desc: 'Drive log, mileage tracking (settings/config are super-admin only)' },
      { id: 'activity',   label: 'Activity Log',    desc: 'All user actions across the system' },
      { id: 'openhouse',  label: 'Open Houses',     desc: 'Open house prospecting planner — scrape, route, follow-up' },
    ],
    defaults: {
      super_admin: { voicenotes: true, vehicle: true, activity: true, openhouse: true },
      admin:       { voicenotes: false, vehicle: true, activity: false, openhouse: true },
      core_crew:   { voicenotes: false, vehicle: false, activity: false, openhouse: true },
      crew:        { voicenotes: false, vehicle: false, activity: false, openhouse: false },
    },
  },
];

// ── Build initial state from defaults ──────────────────────────────────────
function buildInitialState() {
  const state = {};
  for (const f of FEATURES) {
    state[f.key] = {};
    for (const r of ROLES) {
      state[f.key][r.id] = f.defaults[r.id];
    }
  }
  return state;
}

// ── Helpers ────────────────────────────────────────────────────────────────
function getLevelIndex(feature, levelId) {
  if (feature.independent) return -1;
  return feature.levels.findIndex(l => l.id === levelId);
}

// ── Component ──────────────────────────────────────────────────────────────
export default function RoleMatrixBuilder() {
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();
  const { toast } = useToast();
  const [matrix, setMatrix] = useState(buildInitialState);
  const [dirty, setDirty] = useState(false);
  const loadedRef = useRef(false);

  // Load saved matrix from settings
  useEffect(() => {
    if (loadedRef.current || !settings?.data) return;
    const saved = settings.data.roleMatrix;
    if (saved && typeof saved === 'object') {
      const merged = buildInitialState();
      for (const f of FEATURES) {
        if (saved[f.key] && typeof saved[f.key] === 'object') {
          for (const r of ROLES) {
            if (saved[f.key][r.id] !== undefined) {
              merged[f.key][r.id] = saved[f.key][r.id];
            }
          }
        }
      }
      setMatrix(merged);
      loadedRef.current = true;
    }
  }, [settings]);

  const handleSave = async () => {
    if (!settings?.data) return;
    try {
      await updateSettings.mutateAsync({ data: { ...settings.data, roleMatrix: matrix } });
      setDirty(false);
      toast('Role matrix saved', 'success');
    } catch (err) {
      console.error('[RoleMatrix] Save failed:', err);
      toast('Failed to save role matrix', 'error');
    }
  };

  // Change a level for a hierarchical feature
  const setLevel = (featureKey, roleId, levelId) => {
    if (roleId === 'super_admin') return;
    setDirty(true);
    setMatrix(prev => ({
      ...prev,
      [featureKey]: { ...prev[featureKey], [roleId]: levelId },
    }));
  };

  // Toggle an independent tool
  const toggleTool = (featureKey, roleId, toolId) => {
    if (roleId === 'super_admin') return;
    setDirty(true);
    setMatrix(prev => {
      const current = prev[featureKey][roleId] || {};
      return {
        ...prev,
        [featureKey]: {
          ...prev[featureKey],
          [roleId]: { ...current, [toolId]: !current[toolId] },
        },
      };
    });
  };

  const resetDefaults = () => { setMatrix(buildInitialState()); setDirty(true); };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-text-primary">Role Permissions Matrix</h3>
          <p className="text-xs text-text-dim mt-1">
            Pick one access level per role for each feature. Higher levels include everything below.
            {dirty && <span className="ml-2 text-yellow-400/70 font-medium">● unsaved changes</span>}
          </p>
        </div>
        <button onClick={resetDefaults}
          className="text-xs px-3 py-1.5 rounded border border-border text-text-dim hover:text-text-primary hover:border-accent/50 transition-colors">
          Reset to Defaults
        </button>
      </div>

      {/* Role legend */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {ROLES.map(r => (
          <div key={r.id} className="flex items-center gap-2 px-3 py-2 rounded bg-surface-2/50 border border-border/30">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: r.color }} />
            <div className="min-w-0">
              <div className="text-xs font-semibold text-text-primary truncate">{r.label}</div>
              <div className="text-2xs text-text-dim truncate">{r.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Feature rows */}
      <div className="overflow-x-auto"><div className="space-y-2 min-w-[560px]">
        {FEATURES.map(feature => {
          const isIndependent = feature.independent;

          return (
            <div key={feature.key} className="border border-border/50 rounded-lg overflow-hidden">
              {/* Feature header */}
              <div className="px-3 py-2 bg-surface-2/60 border-b border-border/30">
                <div className="flex items-center gap-2">
                  <span className="text-sm">{feature.icon}</span>
                  <span className="text-xs font-semibold text-text-primary">{feature.label}</span>
                  {feature.desc && <span className="text-2xs text-text-dim">— {feature.desc}</span>}
                </div>
              </div>

              {/* Role columns */}
              <div className="grid grid-cols-4 divide-x divide-border/30">
                {ROLES.map(r => {
                  const currentValue = matrix[feature.key]?.[r.id];
                  const locked = r.id === 'super_admin';

                  return (
                    <div key={r.id} className="px-2 py-2">
                      {/* Role label */}
                      <div className="text-2xs font-bold uppercase tracking-wider mb-1.5 text-center"
                           style={{ color: r.color }}>
                        {r.short}
                      </div>

                      {isIndependent ? (
                        /* Independent tools — checkboxes */
                        <div className="space-y-1">
                          {feature.items.map(tool => {
                            const checked = locked ? true : !!currentValue?.[tool.id];
                            return (
                              <button key={tool.id}
                                disabled={locked}
                                onClick={() => toggleTool(feature.key, r.id, tool.id)}
                                className={`w-full text-left px-1.5 py-1 rounded text-2xs transition-colors flex items-center gap-1.5 ${
                                  locked ? 'opacity-50 cursor-not-allowed' :
                                  checked ? 'cursor-pointer' : 'text-text-dim cursor-pointer hover:bg-surface-2/50'
                                }`}
                                style={checked ? { background: r.color + '15', color: r.color } : undefined}
                                title={tool.desc}>
                                <span className="flex-shrink-0">{checked ? '✓' : '○'}</span>
                                <span className="truncate">{tool.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        /* Hierarchical levels — radio-style buttons */
                        <div className="space-y-0.5">
                          {feature.levels.map((level, idx) => {
                            const isSelected = currentValue === level.id;
                            const highestIdx = feature.levels.length - 1;
                            const isLockedLevel = locked && idx === highestIdx;

                            return (
                              <button key={level.id}
                                disabled={locked}
                                onClick={() => setLevel(feature.key, r.id, level.id)}
                                className={`w-full text-left px-1.5 py-1 rounded text-2xs transition-all flex items-center gap-1.5 ${
                                  locked && !isLockedLevel ? 'hidden' :
                                  locked ? 'opacity-50 cursor-not-allowed' :
                                  isSelected ? 'cursor-pointer font-medium' :
                                  'text-text-dim cursor-pointer hover:bg-surface-2/50'
                                }`}
                                style={isSelected && !locked ? { background: r.color + '18', color: r.color, borderLeft: `2px solid ${r.color}` } :
                                       isLockedLevel ? { background: r.color + '18', color: r.color, borderLeft: `2px solid ${r.color}` } :
                                       { borderLeft: '2px solid transparent' }}
                                title={level.desc}>
                                <span className="flex-shrink-0 w-3 text-center">
                                  {(isSelected || isLockedLevel) ? '●' : '○'}
                                </span>
                                <span className="truncate">{level.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div></div>

      {/* Save bar */}
      <div className="flex items-center justify-between gap-4 px-3 py-2 bg-surface-2/50 border border-border/50 rounded">
        <span className="text-xs text-text-dim">Active — changes apply immediately for all users.</span>
        <button onClick={handleSave}
          disabled={!dirty || updateSettings.isPending}
          className={`px-4 py-1.5 rounded text-xs font-medium transition-colors ${
            dirty ? 'bg-accent text-white hover:bg-accent/80'
                  : 'bg-surface border border-border text-text-dim cursor-not-allowed'
          }`}>
          {updateSettings.isPending ? 'Saving...' : dirty ? 'Save Changes' : 'Saved ✓'}
        </button>
      </div>
    </div>
  );
}

export { ROLES, FEATURES };
